// Streams MP3 bytes into the shared AudioContext via the two-decode
// pattern validated by the iOS spike (public/voice-spike.html, branch
// spike/ios-streaming-audio). Once DECODE_THRESHOLD_BYTES have
// accumulated we decode-and-schedule a partial buffer to start
// playback ASAP; after the stream ends we decode the full buffer and
// schedule the remainder with an offset equal to the first decode's
// duration. Short clips that complete before the threshold take a
// single-decode fallback path.

const DECODE_THRESHOLD_BYTES = 24 * 1024;
const SCHEDULE_CUSHION_SEC = 0.02;

export default class TTSPlayer {
  constructor(audioSessionManager, telemetry = null) {
    this._asm = audioSessionManager;
    this._telemetry = telemetry || null;
    this._state = 'idle';
    this._listeners = new Map();
    this._currentReader = null;
    this._cancelled = false;
    this._activeSources = [];
  }

  getState() {
    return this._state;
  }

  on(event, callback) {
    if (!this._listeners.has(event)) this._listeners.set(event, new Set());
    this._listeners.get(event).add(callback);
  }

  off(event, callback) {
    const set = this._listeners.get(event);
    if (set) set.delete(callback);
  }

  _emit(event, payload) {
    const set = this._listeners.get(event);
    if (!set) return;
    for (const cb of set) {
      try {
        cb(payload);
      } catch (e) {
        console.error('[TTSPlayer] listener threw:', e);
      }
    }
  }

  _setState(next) {
    if (this._state === next) return;
    this._state = next;
    this._emit('stateChange', next);
  }

  cancel() {
    if (this._state === 'idle' || this._state === 'ended') return;
    this._cancelled = true;
    if (this._currentReader) {
      try {
        this._currentReader.cancel();
      } catch {}
    }
    for (const src of this._activeSources) {
      try {
        src.onended = null;
        src.stop();
      } catch {}
    }
    this._activeSources = [];
    this._setState('cancelled');
    this._emit('cancel');
  }

  async play(stream) {
    this._cancelled = false;
    this._activeSources = [];
    this._setState('loading');

    const startMs = performance.now();
    const ctx = this._asm.getContext();
    const reader = stream.getReader();
    this._currentReader = reader;

    const chunks = [];
    let totalBytes = 0;
    let firstDecodeDone = false;
    let firstDecodedDuration = 0;
    let firstScheduleEndAt = null;
    let audibleFired = false;
    let ttfaMs = null;

    const playbackEnded = makeDeferred();

    const concat = (parts) => {
      let total = 0;
      for (const c of parts) total += c.byteLength;
      const out = new Uint8Array(total);
      let off = 0;
      for (const c of parts) {
        out.set(c, off);
        off += c.byteLength;
      }
      return out;
    };

    const watchAudible = (startAt) => {
      (async () => {
        while (!this._cancelled && ctx.currentTime < startAt) {
          await new Promise((r) => requestAnimationFrame(r));
        }
        if (this._cancelled || audibleFired) return;
        audibleFired = true;
        ttfaMs = performance.now() - startMs;
        this._setState('playing');
        this._emit('audible', { ttfaMs });
      })();
    };

    const scheduleBuffer = (buffer, startAt, offsetSec, isFinal) => {
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.connect(this._asm.getDestinationNode());
      src.start(startAt, offsetSec || 0);
      this._activeSources.push(src);
      const willPlayFor = buffer.duration - (offsetSec || 0);
      const endAt = startAt + willPlayFor;
      if (isFinal) {
        src.onended = () => {
          if (this._cancelled) return;
          const totalMs = performance.now() - startMs;
          this._setState('ended');
          this._emit('end', {
            totalMs,
            audioDurationMs: buffer.duration * 1000,
          });
          playbackEnded.resolve();
        };
      }
      return endAt;
    };

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (this._cancelled) {
          playbackEnded.resolve();
          return;
        }
        if (done) break;
        chunks.push(value);
        totalBytes += value.byteLength;

        if (!firstDecodeDone && totalBytes >= DECODE_THRESHOLD_BYTES) {
          const buf = concat(chunks).buffer.slice(0);
          try {
            const decoded = await ctx.decodeAudioData(buf);
            if (this._cancelled) {
              playbackEnded.resolve();
              return;
            }
            firstDecodeDone = true;
            firstDecodedDuration = decoded.duration;
            const startAt = Math.max(
              ctx.currentTime,
              ctx.currentTime + SCHEDULE_CUSHION_SEC
            );
            firstScheduleEndAt = scheduleBuffer(decoded, startAt, 0, false);
            watchAudible(startAt);
          } catch {
            // Likely a truncated MP3 frame — keep accumulating bytes
            // and retry on the final decode.
          }
        }
      }

      if (this._cancelled) {
        playbackEnded.resolve();
        return;
      }

      const fullBuf = concat(chunks).buffer.slice(0);
      const decodedFull = await ctx.decodeAudioData(fullBuf);

      if (this._cancelled) {
        playbackEnded.resolve();
        return;
      }

      if (!firstDecodeDone) {
        // Single-decode fallback: stream completed before threshold.
        const startAt = Math.max(
          ctx.currentTime,
          ctx.currentTime + SCHEDULE_CUSHION_SEC
        );
        scheduleBuffer(decodedFull, startAt, 0, true);
        watchAudible(startAt);
      } else if (decodedFull.duration > firstDecodedDuration + 0.01) {
        scheduleBuffer(
          decodedFull,
          firstScheduleEndAt,
          firstDecodedDuration,
          true
        );
      } else {
        // First decode already covered the whole clip — onended on the
        // partial source wasn't wired, so approximate end with a timer
        // tied to the buffer's end on the audio timeline.
        const remainingMs =
          (firstScheduleEndAt - ctx.currentTime) * 1000 + 50;
        setTimeout(() => {
          if (this._cancelled) return;
          const totalMs = performance.now() - startMs;
          this._setState('ended');
          this._emit('end', {
            totalMs,
            audioDurationMs: firstDecodedDuration * 1000,
          });
          playbackEnded.resolve();
        }, Math.max(0, remainingMs));
      }

      await playbackEnded.promise;
    } catch (error) {
      if (this._cancelled) return;
      for (const src of this._activeSources) {
        try {
          src.onended = null;
          src.stop();
        } catch {}
      }
      this._activeSources = [];
      this._setState('error');
      this._emit('error', { error });
      this._telemetry?.track('tts.read_failed', {
        totalBytes,
        firstDecodeDone,
        firstDecodedDuration,
        errorName: error?.name,
        errorMessage: error?.message,
      });
      throw error;
    } finally {
      this._currentReader = null;
    }
  }
}

function makeDeferred() {
  let resolve, reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
}
